import { lazy, Suspense, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ClipboardCheck,
  Clock,
  FilePlus2,
  Pencil,
  Printer,
  Send,
  ShieldCheck,
  Tag,
  Users,
  XCircle,
} from 'lucide-react';

import {
  getSop,
  editSopDraft,
  openSopRevision,
  submitSopVersion,
  approveSopVersion,
  rejectSopVersion,
  describeSopError,
  formatSopIdentifier,
  getComprehensionTest,
  getSopReackStatus,
  serviceLineLabel,
  type SopDetail,
  type SopVersionState,
  type ServiceLineTag,
  type ApplicabilityView,
  type ReackStatus,
} from '@/api/sops.api';
import type { TipTapDoc } from '@/components/richtext/TipTapRenderer';
import { acknowledgeSop, getMyAcks } from '@/api/training.api';
import { ComprehensionTestCard } from '@/components/compliance/ComprehensionTestCard';
import { ComprehensionTestTaker } from '@/components/compliance/ComprehensionTestTaker';
import { RevisionRequestsCard } from '@/components/compliance/RevisionRequestsCard';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { SopRenderer } from '@/components/compliance/SopRenderer';
import { SopTagsModal } from '@/components/compliance/SopTagsModal';
import { ApplicabilityModal } from '@/components/compliance/ApplicabilityModal';
import { describeTrainingError } from '@/lib/training/errors';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';

// The TipTap editor bundle loads on demand — only when an editor opens the
// content editor, never for the read-only / acknowledge experience.
const RichTextEditor = lazy(() => import('@/components/richtext/RichTextEditor'));

/** Compliance authors (mirrors the server's `requireComplianceRole()` set). */
const EDITOR_ROLES = ['visionary', 'integrator', 'quality_lead'];

/**
 * Detail view for a single SOP.
 *
 * Every employee reads the approved revision and (unless they're an auditor
 * seat) acknowledges it — that path is unchanged. Compliance authors
 * (visionary / integrator / quality_lead) additionally get the authoring +
 * approval workflow (ADR-036): a working draft/pending revision shown beside
 * the in-effect approved one, plus Edit / Submit / Approve / Reject / New
 * revision actions. Each mutation is change-controlled (reasonForChange) and
 * approvals are Part-11 signatures.
 */
export function SopDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const { state: authState } = useAuth();

  const revisionParam = searchParams.get('revision');
  const parsedRevision = revisionParam ? Number.parseInt(revisionParam, 10) : Number.NaN;
  const revisionOpt = Number.isFinite(parsedRevision) && parsedRevision > 0 ? parsedRevision : undefined;

  const [tagsModalOpen, setTagsModalOpen] = useState(false);
  const [applicabilityOpen, setApplicabilityOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [newRevOpen, setNewRevOpen] = useState(false);

  const {
    data: sop,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['compliance-sop', slug, revisionOpt ?? 'latest'],
    queryFn: () => getSop(slug ?? '', revisionOpt !== undefined ? { revision: revisionOpt } : undefined),
    enabled: Boolean(slug),
  });

  // Only integrator+visionary+quality_lead can author. Mirrors the server
  // gate (`requireComplianceRole()` default set).
  const isEditor =
    authState.status === 'authenticated' && EDITOR_ROLES.includes(authState.me.membership.role);

  // Auditor seats are external inspector logins — they read SOPs but never
  // sign personnel acknowledgments (ADR-025).
  const canAcknowledge =
    authState.status === 'authenticated' && authState.me.membership.role !== 'auditor_readonly';

  const myFullName =
    authState.status === 'authenticated'
      ? `${authState.me.user.firstName} ${authState.me.user.lastName}`.trim()
      : '';

  // Derived workflow state from the (revision-descending) history strip.
  const history = sop?.history ?? [];
  const latest = history[0] ?? null;
  const approvedEntry = history.find((h) => h.state === 'approved') ?? null;

  const viewingLatest = revisionOpt === undefined || (latest !== null && revisionOpt === latest.revision);

  // When an editor is reviewing an open (draft/pending) revision and an
  // approved one already exists, fetch the approved content so we can show
  // the in-effect version beside the proposed change.
  const compareRevision = approvedEntry?.revision;
  const workingState = sop?.currentVersion.state;
  const isWorkingView =
    isEditor && viewingLatest && (workingState === 'draft' || workingState === 'pending_approval');
  const showCompare =
    isWorkingView && compareRevision !== undefined && compareRevision !== sop?.currentVersion.revision;

  const compareQuery = useQuery({
    queryKey: ['compliance-sop', slug, 'compare', compareRevision],
    queryFn: () =>
      getSop(slug ?? '', compareRevision !== undefined ? { revision: compareRevision } : undefined),
    enabled: Boolean(slug) && showCompare,
  });

  // Re-acknowledgment progress (ADR-038) — author-only, and only meaningful
  // for the in-effect (approved) revision. Gated so we never fetch it for
  // employees or for draft/pending views.
  // ADR-040: an ACTIVE comprehension test REPLACES the acknowledge button —
  // the server refuses a bare acknowledge while one gates the SOP, so showing
  // the button would hand the reader a guaranteed 409.
  const comprehensionQuery = useQuery({
    queryKey: ['sop-comprehension', slug],
    queryFn: () => getComprehensionTest(slug ?? ''),
    enabled: Boolean(slug),
  });
  const hasActiveTest = comprehensionQuery.data?.active === true;

  const reackQuery = useQuery({
    queryKey: ['sop-reack', slug],
    queryFn: () => getSopReackStatus(slug ?? ''),
    enabled: Boolean(slug) && isEditor && sop?.currentVersion.state === 'approved',
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <div className="size-8 animate-spin rounded-full border-2 border-kw-cerulean border-t-transparent" />
      </div>
    );
  }

  if (error || !sop) {
    return (
      <Card className="flex items-start gap-3 border-kw-danger-text/30 bg-kw-danger-bg p-4">
        <AlertTriangle className="size-5 shrink-0 text-kw-danger-text" />
        <div className="flex-1">
          <p className="text-[14px] font-medium text-kw-danger-text">SOP not available</p>
          <p className="mt-1 text-[13px] text-kw-danger-text/80">
            {error instanceof Error
              ? error.message
              : 'This SOP does not exist or you do not have access to it.'}
          </p>
          <Link
            to="/compliance/sops"
            className="mt-3 inline-block text-[13px] text-kw-cerulean hover:underline"
          >
            ← Back to all SOPs
          </Link>
        </div>
      </Card>
    );
  }

  const current = sop.currentVersion;
  const workingRevision = current.revision;

  return (
    <div className="mx-auto max-w-3xl space-y-6 print:max-w-none">
      {/* Back link — hidden on print */}
      <div className="print:hidden">
        <Link
          to="/compliance/sops"
          className="inline-flex items-center gap-1 text-[13px] text-kw-muted hover:text-kw-shark"
        >
          <ArrowLeft className="size-4" />
          All SOPs
        </Link>
      </div>

      {/* Header */}
      <header className="space-y-2 border-b border-kw-border pb-4">
        <div className="flex items-baseline justify-between gap-3">
          <p className="font-mono text-[13px] text-kw-muted">
            {formatSopIdentifier({
              family: sop.family,
              number: sop.number,
              revision: current.revision,
            })}
          </p>
          <div className="flex items-center gap-2 print:hidden">
            <StateBadge state={current.state} />
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                window.print();
              }}
              aria-label="Print SOP"
            >
              <Printer className="size-4" />
              Print
            </Button>
          </div>
        </div>
        <h1 className="typo-h1">{sop.title}</h1>

        {/* Service-line tags row */}
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <Tag className="size-3.5 text-kw-muted" />
          {sop.serviceLineTags.length === 0 ? (
            <span className="text-[12px] uppercase tracking-wide text-kw-ghost">No service line</span>
          ) : (
            sop.serviceLineTags.map((tag: ServiceLineTag) => (
              <span
                key={tag}
                className={
                  tag === 'kwf'
                    ? 'rounded-kw-full bg-kw-cerulean-light px-2.5 py-0.5 text-[11px] font-medium text-kw-cerulean-dark'
                    : 'rounded-kw-full bg-kw-emerald-light px-2.5 py-0.5 text-[11px] font-medium text-kw-tag-text'
                }
              >
                {serviceLineLabel(tag)}
              </span>
            ))
          )}
          {isEditor ? (
            <button
              type="button"
              onClick={() => {
                setTagsModalOpen(true);
              }}
              className="text-[12px] text-kw-cerulean hover:text-kw-cerulean-dark print:hidden"
            >
              Edit tags
            </button>
          ) : null}
        </div>

        {sop.importedFromFilename ? (
          <p className="text-[12px] text-kw-muted">
            Imported from <span className="font-mono">{sop.importedFromFilename}</span>
          </p>
        ) : null}
      </header>

      {/* Editorial action bar — authors only. Not printed. */}
      {isEditor ? (
        <EditorActionBar
          slug={sop.slug}
          state={current.state}
          revision={current.revision}
          viewingLatest={viewingLatest}
          onEdit={() => {
            setEditOpen(true);
          }}
          onSubmit={() => {
            setSubmitOpen(true);
          }}
          onApprove={() => {
            setApproveOpen(true);
          }}
          onReject={() => {
            setRejectOpen(true);
          }}
          onNewRevision={() => {
            setNewRevOpen(true);
          }}
        />
      ) : null}

      {/* Applicability — author-only. Who does this procedure apply to?
          (roles / seats / departments; ADR-037). Not printed. */}
      {isEditor ? (
        <AppliesToCard
          applicability={sop.applicability ?? []}
          onEdit={() => {
            setApplicabilityOpen(true);
          }}
        />
      ) : null}

      {/* Re-acknowledgment status — author-only, approved revisions only. Who
          still owes an acknowledgment of the in-effect revision (ADR-038)?
          Not printed. */}
      {isEditor && current.state === 'approved' ? <ReackStatusCard status={reackQuery.data ?? null} /> : null}

      {/* Authoring for the comprehension test (ADR-040). Editor-only; the
          server also gates PUT/suggest behind requireEditor. */}
      {isEditor && slug ? <ComprehensionTestCard slug={slug} /> : null}

      {/* Body — a side-by-side comparison while an author reviews a working
          revision against the in-effect one, otherwise the single article. */}
      {showCompare ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <VersionPanel label="Currently in effect" revision={compareRevision} state="approved">
            {compareQuery.data ? (
              <SopRenderer doc={compareQuery.data.currentVersion.contentJson} />
            ) : (
              <p className="text-[13px] text-kw-muted">Loading the approved revision…</p>
            )}
          </VersionPanel>
          <VersionPanel label="Proposed change" revision={current.revision} state={current.state}>
            <SopRenderer doc={current.contentJson} />
          </VersionPanel>
        </div>
      ) : (
        <article className="rounded-kw-md bg-white p-6 shadow-kw-xs print:p-0 print:shadow-none">
          {isWorkingView && approvedEntry === null ? (
            <p className="mb-4 rounded-kw-md bg-kw-bg-blue px-3 py-2 text-[12px] text-kw-cerulean-dark print:hidden">
              This is the first revision — it becomes the in-effect SOP once approved.
            </p>
          ) : null}
          <SopRenderer doc={current.contentJson} />
        </article>
      )}

      {/* Acknowledgment banner — the 111.14 "personnel have read the
          procedure" record. Only for the currently-approved revision. */}
      {/* Revision requests (ADR-041) — every reader can propose a change and
          see the history of who asked for what. Independent of the
          acknowledge/comprehension gate below. */}
      {slug ? (
        <RevisionRequestsCard
          slug={slug}
          myMembershipId={authState.status === 'authenticated' ? authState.me.membership.id : null}
        />
      ) : null}

      {canAcknowledge && current.state === 'approved' && hasActiveTest && slug ? (
        <ComprehensionTestTaker slug={slug} />
      ) : null}

      {canAcknowledge && current.state === 'approved' && !hasActiveTest ? (
        <AckBanner
          sopId={sop.id}
          sopVersionId={current.id}
          statement={`I have read and understood ${formatSopIdentifier({
            family: sop.family,
            number: sop.number,
          })} rev ${String(current.revision)}`}
        />
      ) : null}

      {/* Change-control footer (Part 11 §11.10(e) intelligibility) */}
      {current.reasonForChange || current.changeSummary ? (
        <Card className="space-y-1 bg-kw-bg-blue p-4 text-[13px]">
          <p className="font-medium text-kw-cerulean-dark">Change control</p>
          {current.changeSummary ? (
            <p className="text-kw-shark">
              <span className="font-medium">Change summary:</span> {current.changeSummary}
            </p>
          ) : null}
          {current.reasonForChange ? (
            <p className="text-kw-shark">
              <span className="font-medium">Reason for change:</span> {current.reasonForChange}
            </p>
          ) : null}
        </Card>
      ) : null}

      {/* Revision history strip — hidden on print */}
      {sop.history.length > 1 ? (
        <Card className="p-4 print:hidden">
          <p className="mb-3 flex items-center gap-2 text-[13px] font-medium text-kw-shark">
            <Clock className="size-4 text-kw-muted" />
            Revision history
          </p>
          <ul className="space-y-1">
            {sop.history.map((h) => (
              <li
                key={h.id}
                className="flex items-center justify-between gap-3 rounded px-2 py-1 text-[13px] hover:bg-kw-bg"
              >
                <Link
                  to={`/compliance/sops/${sop.slug}?revision=${String(h.revision)}`}
                  className="flex-1 text-kw-shark hover:underline"
                >
                  Revision {String(h.revision).padStart(3, '0')}
                </Link>
                <StateBadge state={h.state} />
                <span className="text-[11px] text-kw-muted">
                  {h.publishedAt
                    ? `Published ${new Date(h.publishedAt).toLocaleDateString()}`
                    : 'Not published'}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      {/* ── Author-only modals ──────────────────────────────────────────── */}
      {isEditor ? (
        <SopTagsModal
          open={tagsModalOpen}
          onClose={() => {
            setTagsModalOpen(false);
          }}
          slug={sop.slug}
          initialTags={sop.serviceLineTags}
        />
      ) : null}

      {isEditor && applicabilityOpen ? (
        <ApplicabilityModal
          open
          onClose={() => {
            setApplicabilityOpen(false);
          }}
          slug={sop.slug}
          initialApplicability={sop.applicability ?? []}
        />
      ) : null}

      {isEditor && editOpen ? (
        <EditContentModal
          slug={sop.slug}
          revision={workingRevision}
          initialContent={current.contentJson}
          initialChangeSummary={current.changeSummary}
          onClose={() => {
            setEditOpen(false);
          }}
        />
      ) : null}

      {isEditor && submitOpen ? (
        <ReasonModal
          slug={sop.slug}
          title="Submit for approval"
          description="Move this draft into review. A different reviewer must approve and sign it before it takes effect."
          confirmLabel="Submit for approval"
          successMessage="Submitted for approval"
          run={(reason) => submitSopVersion(sop.slug, workingRevision, { reasonForChange: reason })}
          onClose={() => {
            setSubmitOpen(false);
          }}
        />
      ) : null}

      {isEditor && rejectOpen ? (
        <ReasonModal
          slug={sop.slug}
          title="Reject revision"
          description="Send this revision back to draft so it can be reworked. Explain what needs to change."
          confirmLabel="Reject"
          confirmVariant="danger"
          successMessage="Revision sent back to draft"
          run={(reason) => rejectSopVersion(sop.slug, workingRevision, { reasonForChange: reason })}
          onClose={() => {
            setRejectOpen(false);
          }}
        />
      ) : null}

      {isEditor && newRevOpen ? (
        <ReasonModal
          slug={sop.slug}
          title="Start a new revision"
          description="Open a fresh draft based on the approved revision. The approved SOP stays in effect until the new one is approved."
          confirmLabel="Create draft revision"
          successMessage="New draft revision opened"
          run={(reason) => openSopRevision(sop.slug, { reasonForChange: reason })}
          onClose={() => {
            setNewRevOpen(false);
          }}
        />
      ) : null}

      {isEditor && approveOpen ? (
        <ApproveModal
          slug={sop.slug}
          revision={workingRevision}
          expectedName={myFullName}
          onClose={() => {
            setApproveOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * Author action bar. The available actions follow the state machine —
 * draft → edit / submit; pending → approve / reject; approved → new revision.
 * When viewing a historical (non-latest) revision we surface a return link
 * instead of actions, since only the latest revision is mutable.
 */
function EditorActionBar({
  slug,
  state,
  revision,
  viewingLatest,
  onEdit,
  onSubmit,
  onApprove,
  onReject,
  onNewRevision,
}: {
  slug: string;
  state: SopVersionState;
  revision: number;
  viewingLatest: boolean;
  onEdit: () => void;
  onSubmit: () => void;
  onApprove: () => void;
  onReject: () => void;
  onNewRevision: () => void;
}) {
  if (!viewingLatest) {
    return (
      <Card className="flex flex-wrap items-center justify-between gap-2 bg-kw-bg p-3 print:hidden">
        <p className="text-[13px] text-kw-muted">
          Viewing revision {String(revision).padStart(3, '0')} — historical revisions are read-only.
        </p>
        <Link
          to={`/compliance/sops/${slug}`}
          className="text-[13px] font-medium text-kw-cerulean hover:text-kw-cerulean-dark"
        >
          Back to the latest revision
        </Link>
      </Card>
    );
  }

  return (
    <Card className="flex flex-wrap items-center gap-2 p-3 print:hidden">
      <p className="mr-auto text-[13px] text-kw-muted">
        {state === 'draft'
          ? 'Draft — not visible to employees yet.'
          : state === 'pending_approval'
            ? 'Pending approval — a reviewer must sign off.'
            : state === 'approved'
              ? 'Approved and in effect.'
              : 'Superseded.'}
      </p>
      {state === 'draft' ? (
        <>
          <Button size="sm" variant="secondary" onClick={onEdit}>
            <Pencil className="size-4" />
            Edit content
          </Button>
          <Button size="sm" onClick={onSubmit}>
            <Send className="size-4" />
            Submit for approval
          </Button>
        </>
      ) : null}
      {state === 'pending_approval' ? (
        <>
          <Button size="sm" variant="secondary" onClick={onReject}>
            <XCircle className="size-4" />
            Reject
          </Button>
          <Button size="sm" onClick={onApprove}>
            <ShieldCheck className="size-4" />
            Approve &amp; sign
          </Button>
        </>
      ) : null}
      {state === 'approved' ? (
        <Button size="sm" onClick={onNewRevision}>
          <FilePlus2 className="size-4" />
          New revision
        </Button>
      ) : null}
    </Card>
  );
}

/** Titled panel wrapping a rendered SOP body in the side-by-side compare. */
function VersionPanel({
  label,
  revision,
  state,
  children,
}: {
  label: string;
  revision: number;
  state: SopVersionState;
  children: React.ReactNode;
}) {
  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between gap-2 border-b border-kw-border bg-kw-bg px-4 py-2">
        <span className="text-[12px] font-medium uppercase tracking-wide text-kw-muted">{label}</span>
        <span className="flex items-center gap-2">
          <span className="font-mono text-[11px] text-kw-muted">Rev {String(revision).padStart(3, '0')}</span>
          <StateBadge state={state} />
        </span>
      </div>
      <div className="p-4">{children}</div>
    </Card>
  );
}

/**
 * Edit a draft revision's content. Mounts the lazy TipTap editor seeded with
 * the current draft. A reason for change is required (cGMP) on save.
 */
function EditContentModal({
  slug,
  revision,
  initialContent,
  initialChangeSummary,
  onClose,
}: {
  slug: string;
  revision: number;
  initialContent: TipTapDoc;
  initialChangeSummary: string | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [content, setContent] = useState<TipTapDoc>(initialContent);
  const [changeSummary, setChangeSummary] = useState(initialChangeSummary ?? '');
  const [reason, setReason] = useState('');

  const mutation = useMutation({
    mutationFn: () =>
      editSopDraft(slug, revision, {
        contentJson: content,
        changeSummary: changeSummary.trim() === '' ? undefined : changeSummary.trim(),
        reasonForChange: reason.trim(),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['compliance-sop', slug] });
      void queryClient.invalidateQueries({ queryKey: ['compliance-sops'] });
      toast.show('Draft saved', { variant: 'success' });
      onClose();
    },
  });

  const canSubmit = reason.trim().length > 0 && !mutation.isPending;

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (!canSubmit) return;
    mutation.mutate();
  }

  return (
    <Modal open onClose={onClose} title={`Edit draft — Rev ${String(revision).padStart(3, '0')}`} size="lg">
      <form
        onSubmit={(e) => {
          handleSubmit(e);
        }}
        className="space-y-4"
      >
        <div>
          <span className="mb-1.5 block text-[13px] font-medium text-kw-label">Content</span>
          <Suspense
            fallback={
              <div className="rounded-kw-md border border-kw-border px-3.5 py-6 text-center text-[13px] text-kw-muted">
                Loading editor…
              </div>
            }
          >
            <RichTextEditor value={content} onChange={setContent} />
          </Suspense>
        </div>

        <Input
          label="Change summary"
          name="edit-change-summary"
          maxLength={500}
          placeholder="Optional — a short note shown in the revision history"
          value={changeSummary}
          onChange={(e) => {
            setChangeSummary(e.target.value);
          }}
        />

        <Input
          label="Reason for change"
          name="edit-reason"
          required
          maxLength={500}
          placeholder="e.g. clarified the sampling frequency in step 4"
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
          }}
          hint="Required by cGMP §111.180(c). Captured in the audit log."
        />

        {mutation.error ? (
          <MutationError error={mutation.error} fallback="Could not save the draft." />
        ) : null}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={mutation.isPending} disabled={!canSubmit}>
            Save draft
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Approve + sign a pending revision (Part 11 electronic signature). The typed
 * name must match the approver's name on file; segregation of duties is
 * enforced server-side (a drafter cannot approve their own work).
 */
function ApproveModal({
  slug,
  revision,
  expectedName,
  onClose,
}: {
  slug: string;
  revision: number;
  expectedName: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [signatureName, setSignatureName] = useState('');
  const [reason, setReason] = useState('');

  const mutation = useMutation({
    mutationFn: () =>
      approveSopVersion(slug, revision, {
        signatureName: signatureName.trim(),
        reasonForChange: reason.trim(),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['compliance-sop', slug] });
      void queryClient.invalidateQueries({ queryKey: ['compliance-sops'] });
      void queryClient.invalidateQueries({ queryKey: ['training', 'me', 'acks'] });
      toast.show('Revision approved and published', { variant: 'success' });
      onClose();
    },
  });

  const canSubmit = signatureName.trim().length > 0 && reason.trim().length > 0 && !mutation.isPending;

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (!canSubmit) return;
    mutation.mutate();
  }

  return (
    <Modal open onClose={onClose} title={`Approve Rev ${String(revision).padStart(3, '0')}`} size="sm">
      <form
        onSubmit={(e) => {
          handleSubmit(e);
        }}
        className="space-y-4"
      >
        <p className="rounded-kw-md bg-kw-bg-blue px-3 py-2 text-[13px] text-kw-cerulean-dark">
          Approving this revision publishes it, supersedes the previous approved revision, and makes it the
          version employees must acknowledge. This is a signed record.
        </p>

        <Input
          label="Typed signature"
          name="approve-signature"
          required
          autoComplete="off"
          placeholder={expectedName || 'Your full name'}
          value={signatureName}
          onChange={(e) => {
            setSignatureName(e.target.value);
          }}
          hint="Type your full name exactly as it appears on file to sign."
        />

        <Input
          label="Reason for change"
          name="approve-reason"
          required
          maxLength={500}
          placeholder="e.g. reviewed against the current process and approved"
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
          }}
          hint="Required by cGMP §111.180(c). Captured in the audit log."
        />

        {mutation.error ? (
          <MutationError error={mutation.error} fallback="Could not approve the revision." />
        ) : null}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={mutation.isPending} disabled={!canSubmit}>
            <ShieldCheck className="size-4" />
            Approve &amp; sign
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Shared confirm-with-reason modal for the reason-only transitions (submit,
 * reject, open new revision). A documented reason for change is required.
 */
function ReasonModal({
  slug,
  title,
  description,
  confirmLabel,
  confirmVariant = 'primary',
  successMessage,
  run,
  onClose,
}: {
  slug: string;
  title: string;
  description: string;
  confirmLabel: string;
  confirmVariant?: 'primary' | 'danger';
  successMessage: string;
  run: (reason: string) => Promise<SopDetail>;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [reason, setReason] = useState('');

  const mutation = useMutation({
    mutationFn: () => run(reason.trim()),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['compliance-sop', slug] });
      void queryClient.invalidateQueries({ queryKey: ['compliance-sops'] });
      toast.show(successMessage, { variant: 'success' });
      onClose();
    },
  });

  const canSubmit = reason.trim().length > 0 && !mutation.isPending;

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (!canSubmit) return;
    mutation.mutate();
  }

  return (
    <Modal open onClose={onClose} title={title} size="sm">
      <form
        onSubmit={(e) => {
          handleSubmit(e);
        }}
        className="space-y-4"
      >
        <p className="text-[13px] text-kw-muted">{description}</p>

        <Input
          label="Reason for change"
          name="reason-for-change"
          required
          maxLength={500}
          placeholder="Document the reason for this change"
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
          }}
          hint="Required by cGMP §111.180(c). Captured in the audit log."
        />

        {mutation.error ? <MutationError error={mutation.error} fallback="That action failed." /> : null}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant={confirmVariant === 'danger' ? 'danger' : 'primary'}
            isLoading={mutation.isPending}
            disabled={!canSubmit}
          >
            {confirmLabel}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function MutationError({ error, fallback }: { error: unknown; fallback: string }) {
  return (
    <div className="flex items-start gap-2 rounded-kw-md border border-kw-danger-text/30 bg-kw-danger-bg p-3">
      <AlertTriangle className="mt-0.5 size-4 shrink-0 text-kw-danger-text" />
      <p className="text-[12px] text-kw-danger-text">{describeSopError(error, fallback)}</p>
    </div>
  );
}

/**
 * Acknowledge card for the approved revision. Reads the member's own ack
 * history (`/api/training/me/acks`) to decide between the "sign" and
 * "already signed" states; signing POSTs the ack and refreshes both the
 * history and the My Training / My Day pending lists.
 */
function AckBanner({
  sopId,
  sopVersionId,
  statement,
}: {
  sopId: string;
  sopVersionId: string;
  statement: string;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const acksQuery = useQuery({ queryKey: ['training', 'me', 'acks'], queryFn: getMyAcks });
  const existingAck = acksQuery.data?.find((a) => a.sopVersionId === sopVersionId) ?? null;

  const ackMutation = useMutation({
    mutationFn: () => acknowledgeSop(sopId, { source: 'sop_page' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['training', 'me', 'acks'] });
      void queryClient.invalidateQueries({ queryKey: ['training', 'me'] });
      toast.show('Acknowledgment recorded', { variant: 'success' });
    },
    onError: (err) => {
      toast.show(describeTrainingError(err, 'Could not record the acknowledgment.'), {
        variant: 'danger',
      });
    },
  });

  if (acksQuery.isLoading) return null;

  if (existingAck) {
    return (
      <Card className="flex items-start gap-3 border-kw-tag-text/30 bg-kw-emerald-light p-4 print:hidden">
        <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-kw-tag-text" />
        <div>
          <p className="text-[14px] font-medium text-kw-tag-text">Acknowledged</p>
          <p className="mt-0.5 text-[13px] text-kw-tag-text/80">
            You acknowledged revision {existingAck.sopVersion.revision} on{' '}
            {new Date(existingAck.acknowledgedAt).toLocaleDateString(undefined, {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
            })}
            .
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="flex flex-wrap items-center gap-3 border-kw-cerulean/30 bg-kw-bg-blue p-4 print:hidden">
      <div className="min-w-0 flex-1">
        <p className="text-[14px] font-medium text-kw-cerulean-dark">Acknowledgment required</p>
        <p className="mt-0.5 text-[13px] text-kw-shark">&ldquo;{statement}&rdquo;</p>
      </div>
      <Button
        isLoading={ackMutation.isPending}
        onClick={() => {
          ackMutation.mutate();
        }}
      >
        Acknowledge
      </Button>
    </Card>
  );
}

/**
 * Author-only "Applies to" card — renders the SOP's applicability targets
 * (roles / seats / departments) as chips, each with an "AI" badge when the
 * target came from an accepted AI suggestion. Opens the ApplicabilityModal to
 * edit. Not printed.
 */
function AppliesToCard({
  applicability,
  onEdit,
}: {
  applicability: ApplicabilityView[];
  onEdit: () => void;
}) {
  return (
    <Card className="space-y-3 p-4 print:hidden">
      <div className="flex items-center justify-between gap-2">
        <p className="flex items-center gap-2 text-[13px] font-medium text-kw-shark">
          <Users className="size-4 text-kw-muted" />
          Applies to
        </p>
        <button
          type="button"
          onClick={onEdit}
          className="text-[12px] text-kw-cerulean hover:text-kw-cerulean-dark"
        >
          Edit
        </button>
      </div>
      {applicability.length === 0 ? (
        <p className="text-[13px] text-kw-muted">No applicability set yet</p>
      ) : (
        <ul className="flex flex-wrap gap-2">
          {applicability.map((a) => (
            <li
              key={a.id}
              className="inline-flex items-center gap-1.5 rounded-kw-full bg-kw-cerulean-light px-2.5 py-1 text-[12px] text-kw-cerulean-dark"
            >
              {a.targetLabel}
              {a.aiSuggested ? (
                <span className="rounded-kw-full bg-kw-cerulean px-1.5 text-[9px] font-semibold uppercase tracking-wide text-white">
                  AI
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/**
 * Author-only "Re-acknowledgment status" card (ADR-038). Summarizes how much
 * of the responsible audience has acknowledged the in-effect revision, with a
 * progress bar and a collapsible list of who's still outstanding (an "Overdue"
 * badge once past their due date). SOPs that predate propagation tracking
 * report `total === 0` and get a subtle note instead of the full card. Not
 * printed.
 */
function ReackStatusCard({ status }: { status: ReackStatus | null }) {
  const [expanded, setExpanded] = useState(false);

  // Still loading — render nothing rather than a placeholder skeleton.
  if (!status) return null;

  if (status.total === 0) {
    return (
      <Card className="flex items-center gap-2 p-4 text-[13px] text-kw-muted print:hidden">
        <ClipboardCheck className="size-4 shrink-0 text-kw-ghost" />
        No re-acknowledgment tracking for this revision.
      </Card>
    );
  }

  // `outstanding` is always an array per the server contract; alias it locally.
  const outstanding = status.outstanding;
  const pct = Math.round((status.acknowledged / status.total) * 100);

  return (
    <Card className="space-y-3 p-4 print:hidden">
      <div className="flex items-center justify-between gap-2">
        <p className="flex items-center gap-2 text-[13px] font-medium text-kw-shark">
          <ClipboardCheck className="size-4 text-kw-muted" />
          Re-acknowledgment status
        </p>
        {status.revision !== null ? (
          <span className="font-mono text-[11px] text-kw-muted">
            Rev {String(status.revision).padStart(3, '0')}
          </span>
        ) : null}
      </div>

      <div>
        <div className="mb-1 flex items-baseline justify-between gap-2 text-[12px]">
          <span className="text-kw-shark">
            <span className="font-semibold">{status.acknowledged}</span>
            <span className="text-kw-muted">/{status.total} acknowledged</span>
          </span>
          <span className="text-kw-muted">{pct}%</span>
        </div>
        <div
          className="h-2 overflow-hidden rounded-kw-full bg-kw-bg"
          role="progressbar"
          aria-valuenow={status.acknowledged}
          aria-valuemin={0}
          aria-valuemax={status.total}
        >
          <div
            className="h-full rounded-kw-full bg-kw-cerulean transition-all"
            style={{ width: `${String(pct)}%` }}
          />
        </div>
      </div>

      {outstanding.length === 0 ? (
        <p className="text-[12px] text-kw-tag-text">Everyone has acknowledged this revision.</p>
      ) : (
        <div>
          <button
            type="button"
            onClick={() => {
              setExpanded((v) => !v);
            }}
            aria-expanded={expanded}
            className="flex items-center gap-1 text-[12px] font-medium text-kw-cerulean hover:text-kw-cerulean-dark"
          >
            <ChevronDown
              className={`size-3.5 transition-transform ${expanded ? 'rotate-0' : '-rotate-90'}`}
            />
            {expanded ? 'Hide' : 'Show'} {outstanding.length} outstanding
          </button>
          {expanded ? (
            <ul className="mt-2 space-y-1">
              {outstanding.map((m) => (
                <li
                  key={m.membershipId}
                  className="flex items-center justify-between gap-2 rounded px-2 py-1 text-[13px] hover:bg-kw-bg"
                >
                  <span className="min-w-0 truncate text-kw-shark">{m.name}</span>
                  {m.overdue ? <Badge variant="danger">Overdue</Badge> : null}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      )}
    </Card>
  );
}

function StateBadge({ state }: { state: SopVersionState }) {
  switch (state) {
    case 'approved':
      return <Badge variant="success">Approved</Badge>;
    case 'pending_approval':
      return <Badge variant="warning">Pending</Badge>;
    case 'superseded':
      return <Badge variant="neutral">Superseded</Badge>;
    case 'draft':
    default:
      return <Badge variant="info">Draft</Badge>;
  }
}
